/**
 * EditProfileModal — desktop Dialog / mobile Drawer (matches
 * CreateCommunityModal's responsive pattern) with two panels: profile
 * fields and privacy toggles.
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import PrivacySettings from './PrivacySettings';
import { PROFILE } from '@/constants/testIds';

const TONES = [
  { key: 'coral', className: 'bg-coral' },
  { key: 'yellow', className: 'bg-yellow' },
  { key: 'sage', className: 'bg-sage' },
  { key: 'blue', className: 'bg-blue' },
];

function Field({ label, children }) {
  return (
    <div className="mt-4">
      <label className="text-xs font-semibold uppercase tracking-wide text-subtle">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ModalBody({ profile, onSave, onUpdatePrivacy, onClose }) {
  const [panel, setPanel] = useState('profile');
  const [data, setData] = useState({
    name: profile.name ?? '',
    tagline: profile.tagline ?? '',
    bio: profile.bio ?? '',
    location: profile.location ?? '',
    website: profile.website ?? '',
    tone: profile.tone ?? 'coral',
  });

  const handleSave = async () => {
    try {
      await onSave(data);
      toast('Profile updated');
      onClose();
    } catch (error) {
      toast(error.message || 'We could not update your profile.');
    }
  };

  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-ink">Edit Profile</h2>

      <div className="mt-4 inline-flex rounded-full border border-line bg-cream p-1">
        {[{ key: 'profile', label: 'Profile' }, { key: 'privacy', label: 'Privacy' }].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setPanel(tab.key)}
            className={cx(
              'rounded-full px-4 py-1.5 text-xs font-semibold transition-colors',
              panel === tab.key ? 'bg-coral text-[#FFF7F0]' : 'text-subtle hover:text-ink'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {panel === 'profile' ? (
        <div>
          <Field label="Display Name">
            <Input value={data.name} onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))} className="rounded-xl border-line bg-cream" />
          </Field>
          <Field label="Tagline">
            <Input value={data.tagline} onChange={(e) => setData((d) => ({ ...d, tagline: e.target.value }))} placeholder="Reader & Writer" className="rounded-xl border-line bg-cream" />
          </Field>
          <Field label="Bio">
            <Textarea
              value={data.bio}
              onChange={(e) => setData((d) => ({ ...d, bio: e.target.value }))}
              placeholder="Books, coffee & stories that stay with me."
              rows={3}
              className="rounded-xl border-line bg-cream text-sm"
            />
          </Field>
          <Field label="Location">
            <Input value={data.location} onChange={(e) => setData((d) => ({ ...d, location: e.target.value }))} placeholder="Mumbai, India" className="rounded-xl border-line bg-cream" />
          </Field>
          <Field label="Website">
            <Input value={data.website} onChange={(e) => setData((d) => ({ ...d, website: e.target.value }))} placeholder="yourname.com" className="rounded-xl border-line bg-cream" />
          </Field>
          <Field label="Avatar Accent">
            <div className="flex gap-2.5">
              {TONES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setData((d) => ({ ...d, tone: t.key }))}
                  aria-label={`${t.key} accent`}
                  className={cx('h-7 w-7 rounded-full ring-offset-2 ring-offset-card transition-all', t.className, data.tone === t.key ? 'ring-2 ring-ink' : 'opacity-70 hover:opacity-100')}
                />
              ))}
            </div>
          </Field>

          <div className="mt-6 flex justify-end gap-2.5">
            <Button variant="secondary" icon={false} onClick={onClose}>Cancel</Button>
            <Button variant="primary" icon={false} onClick={handleSave}>Save Changes</Button>
          </div>
        </div>
      ) : (
        <div className="mt-5">
          <PrivacySettings
            privacy={profile.privacy}
            onChange={(updates) => onUpdatePrivacy(updates).catch((error) => toast(error.message || 'We could not update your privacy settings.'))}
          />
          <div className="mt-6 flex justify-end">
            <Button variant="primary" icon={false} onClick={onClose}>Done</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EditProfileModal({ isOpen, onClose, profile, onSave, onUpdatePrivacy }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent data-testid={PROFILE.editModal} className="max-h-[88vh] w-[92vw] max-w-lg overflow-y-auto rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">Edit Profile</DialogTitle>
          <ModalBody profile={profile} onSave={onSave} onUpdatePrivacy={onUpdatePrivacy} onClose={onClose} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent data-testid={PROFILE.editModal} className="max-h-[92vh] overflow-y-auto border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">Edit Profile</DrawerTitle>
        <ModalBody profile={profile} onSave={onSave} onUpdatePrivacy={onUpdatePrivacy} onClose={onClose} />
      </DrawerContent>
    </Drawer>
  );
}
